import { SetStateAction, useCallback, useContext, useState } from "react";
import { toast } from "react-toastify";
import Image from "next/image";
import { useRouter } from "next/router";
import { Button } from "components/atoms/Button";
import { ButtonType } from "components/atoms/Button/types";
import { QuestionIcon } from "components/atoms/Icon";
import { Input } from "components/atoms/Input";
import { Modal } from "components/atoms/Modal/index";
import Spinner from "components/atoms/Spinner";
import { EnhancedModelTooltip } from "components/molecules/Tooltip";
import { editImage, poll, pollEditImage } from "lib/actions";
import { AppContext } from "lib/contexts";
import { AppReducerActionType, EditType, UserPremiumType } from "types";
import { Span, Title } from "utils/styling/typography.styled";
import { ButtonContainer, FieldGroup, FormGroup, GoldCost, Icon, Label, ModalContainer, SliderWrapper, TextArea } from "./ADetailerModal.styled";
interface ADetailerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit?: (editRequest: string, isPremium: boolean, editType: string, goldCost: number, negativePrompt: string, cfgScale: number, numDetections: number) => void;
  imageId: string;
  isPremium: boolean;
}
export const ADetailerModal: React.FC<ADetailerModalProps> = ({
  isOpen,
  onClose,
  isPremium,
  imageId,
  onSubmit
}) => {
  const router = useRouter();
  const {
    state,
    dispatch
  } = useContext(AppContext);
  const [editRequest, setEditRequest] = useState("");
  const [negativePrompt, setNegativePrompt] = useState("");
  const [cfgScale, setCfgScale] = useState<number>(7); // Default value
  const [numDetections, setNumDetections] = useState<number>(1); // Default value
  const [loading, setLoading] = useState(false); // Loading state

  const handleEditSubmit = useCallback(async (editRequest: string, isPremium: boolean, imageId: string, editType: string, negativePrompt?: string, cfgScale?: number, numDetections?: number) => {
    if (!state.isLoggedIn) {
      dispatch({
        type: AppReducerActionType.SHOW_AUTH_MODAL,
        payload: {
          ...state,
          showAuthModal: true
        }
      });
      return;
    }
    if (state.userPremiumLevel === UserPremiumType.NONE) {
      dispatch({
        type: AppReducerActionType.SHOW_SUBSCRIPTION_TABLE,
        payload: {
          ...state,
          showSubscriptionTable: true
        }
      });
      toast.error("You need to be a subscribed user to use this feature.");
      return;
    }
    if (state.gold < 1) {
      dispatch({
        type: AppReducerActionType.SHOW_PRICING_TABLE,
        payload: {
          ...state,
          showPricingTable: true
        }
      });
      return;
    }
    const formData = new FormData();
    formData.append("editRequest", editRequest);
    formData.append("imageId", imageId);
    formData.append("premium", isPremium.toString());
    formData.append("editType", editType);
    if (negativePrompt) {
      formData.append("negativePrompt", negativePrompt);
    }
    if (cfgScale) {
      formData.append("cfgScale", cfgScale.toString());
    }
    if (numDetections) {
      formData.append("numDetections", numDetections.toString());
    }
    setLoading(true);
    const {
      requestId,
      error
    } = await editImage(formData);
    if (error) {
      toast.error("An error occurred while submitting the edit request.");
      return;
    }
    if (requestId) {
      const {
        image,
        error
      } = await poll(async () => {
        const result = await pollEditImage(requestId);
        return result;
      }, ({
        image,
        error
      }) => {
        // Continue polling only if there is no image and no critical error
        if (!!image) return false; // Stop polling when an image is retrieved
        if (typeof error === "string") return false;
        if (typeof error === "object") return false;
        return true; // Continue polling otherwise
      }, 5000);
      if (error) {
        if (typeof error === "object") {
          toast.error(error.message || "An unknown error occurred while generating the image and your gold has been refunded. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 1
            }
          });
        } else if (typeof error === "string") {
          toast.error("The request timed out. Please try again.");
          dispatch({
            type: AppReducerActionType.SET_GOLD,
            payload: {
              ...state,
              gold: state.gold + 1
            }
          });
        }
      }
      if (image && image.length > 0) {
        const newImage = Array.isArray(image) ? image[0] : image;

        // Redirect to the new image's page
        router.push(`/images/${newImage.id}`);
        dispatch({
          type: AppReducerActionType.DEC_GOLD,
          payload: state
        });
      }
    }
    setLoading(false);
  }, [router, dispatch, state]);
  const handleSubmit = async () => {
    if (onSubmit) {
      // If an onSubmit function is provided, use it
      await onSubmit(editRequest, isPremium, EditType.ADETAILER, 1, negativePrompt, cfgScale, numDetections);
    } else {
      // Otherwise, use the internal handleEditSubmit function
      await handleEditSubmit(editRequest, isPremium, imageId, EditType.ADETAILER, negativePrompt, cfgScale, numDetections);
    }
    setEditRequest("");
    setNegativePrompt("");
    setCfgScale(7);
    setNumDetections(1);
    onClose();
  };
  const handleClose = () => {
    setEditRequest("");
    setNegativePrompt("");
    setCfgScale(7);
    setNumDetections(1);
    setLoading(false);
    onClose();
  };
  return <Modal open={isOpen} handleBackdropClick={onClose} disableBackdropClick data-sentry-element="Modal" data-sentry-component="ADetailerModal" data-sentry-source-file="index.tsx">
            <EnhancedModelTooltip id="promptTip" title="Text Prompt" details={["Describe the face part of the image you want to fix, including features such as hair colour, eye colour, facial hair, etc.", "E.g. 'realistic, human, male, short brown hair, blue eyes, no facial hair, close up portrait'.", "You can remove details about the background and other scenery", "keep style modifiers.", "Add 'close-up portrait' to the prompt."]} isElite={true} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="negativePromptTip" title="Negative Prompt" details={["List anything you don't want to see. E.g. 'deformed face, freckles'.."]} isElite={true} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="faceDetectionTip" title="Face Detection" details={["Select how many faces you want to detect to fix.", "Each face detected will be fixed separately."]} isElite={true} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <EnhancedModelTooltip id="cfgScaleTip" title="CFG Scale (Creativity)" details={["Describe the creativity the model should use to fix the face.", "Higher values will be more creative, but may not follow the prompt as closely.", "Lower values will be more literal, but may not be as creative."]} isElite={true} data-sentry-element="EnhancedModelTooltip" data-sentry-source-file="index.tsx" />
            <ModalContainer data-sentry-element="ModalContainer" data-sentry-source-file="index.tsx">
                <Title data-sentry-element="Title" data-sentry-source-file="index.tsx">Refine Faces</Title>
                <hr />
                <FormGroup data-sentry-element="FormGroup" data-sentry-source-file="index.tsx">
                    <FieldGroup data-sentry-element="FieldGroup" data-sentry-source-file="index.tsx">
                        <Label htmlFor="prompt" data-sentry-element="Label" data-sentry-source-file="index.tsx">Edit Request:</Label>
                        <Icon id="modelTypeIcon" aria-label="Information about model types" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-sentry-element="Icon" data-sentry-source-file="index.tsx">
                            <Image priority height={24} width={24} src={QuestionIcon} alt="Tooltip" data-tooltip-id="promptTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                        </Icon>
                        <TextArea id="editRequest" name="editRequest" value={editRequest} onChange={(e: {
            target: {
              value: SetStateAction<string>;
            };
          }) => setEditRequest(e.target.value)} placeholder="Enter a detailed description of the changes you want to make to the face, including facial features and art styles, eg. 'realistic, human, male, short brown hair, blue eyes, no facial hair, close up portrait'" data-sentry-element="TextArea" data-sentry-source-file="index.tsx" />
                    </FieldGroup>
                </FormGroup>
                <FormGroup data-sentry-element="FormGroup" data-sentry-source-file="index.tsx">
                    <FieldGroup data-sentry-element="FieldGroup" data-sentry-source-file="index.tsx">
                        <Label htmlFor="negativePrompt" data-sentry-element="Label" data-sentry-source-file="index.tsx">Negative Prompt:</Label>
                        <Icon id="modelTypeIcon" aria-label="Information about model types" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-sentry-element="Icon" data-sentry-source-file="index.tsx">
                            <Image priority height={24} width={24} src={QuestionIcon} alt="Tooltip" data-tooltip-id="negativePromptTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                        </Icon>
                        <TextArea id="negativePrompt" name="negativePrompt" value={negativePrompt} onChange={(e: {
            target: {
              value: SetStateAction<string>;
            };
          }) => setNegativePrompt(e.target.value)} placeholder="Enter descriptions of what you want to avoid in the image." data-sentry-element="TextArea" data-sentry-source-file="index.tsx" />
                    </FieldGroup>
                </FormGroup>
                <FormGroup data-sentry-element="FormGroup" data-sentry-source-file="index.tsx">
                    <FieldGroup data-sentry-element="FieldGroup" data-sentry-source-file="index.tsx">
                        <Label htmlFor="cfgScale" data-sentry-element="Label" data-sentry-source-file="index.tsx">CFG Scale:</Label>
                        <Icon id="modelTypeIcon" aria-label="Information about model types" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-sentry-element="Icon" data-sentry-source-file="index.tsx">
                            <Image priority height={24} width={24} src={QuestionIcon} alt="Tooltip" data-tooltip-id="cfgScaleTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                        </Icon>
                        <SliderWrapper data-sentry-element="SliderWrapper" data-sentry-source-file="index.tsx">
                            <Input type="range" id="cfgScale" min="1" max="50" step="0.1" value={cfgScale} onChange={e => setCfgScale(parseFloat(e.target.value))} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                            <Input type="number" id="cfgScaleNumber" min="1" max="50" step="0.1" value={cfgScale} onChange={e => setCfgScale(parseFloat(e.target.value))} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                        </SliderWrapper>
                    </FieldGroup>
                </FormGroup>
                <FormGroup data-sentry-element="FormGroup" data-sentry-source-file="index.tsx">
                    <FieldGroup data-sentry-element="FieldGroup" data-sentry-source-file="index.tsx">
                        <Label htmlFor="numDetections" data-sentry-element="Label" data-sentry-source-file="index.tsx">
                            Number of Detections:
                        </Label>
                        <Icon id="modelTypeIcon" aria-label="Information about model types" onClick={e => {
            e.preventDefault();
            e.stopPropagation();
          }} data-sentry-element="Icon" data-sentry-source-file="index.tsx">
                            <Image priority height={24} width={24} src={QuestionIcon} alt="Tooltip" data-tooltip-id="faceDetectionTip" data-sentry-element="Image" data-sentry-source-file="index.tsx" />
                        </Icon>
                        <SliderWrapper data-sentry-element="SliderWrapper" data-sentry-source-file="index.tsx">
                            <Input type="range" id="numDetections" min="1" max="10" step="1" value={numDetections} onChange={e => setNumDetections(parseInt(e.target.value))} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                            <Input type="number" id="numDetectionsNumber" min="1" max="10" step="1" value={numDetections} onChange={e => setNumDetections(parseInt(e.target.value))} data-sentry-element="Input" data-sentry-source-file="index.tsx" />
                        </SliderWrapper>
                    </FieldGroup>
                </FormGroup>
                <ButtonContainer data-sentry-element="ButtonContainer" data-sentry-source-file="index.tsx">
                    <Button type="button" variant={ButtonType.SUCCESS} onClick={handleSubmit} disabled={loading} // Disable the button while loading
        data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        {loading ? <Spinner size={24} color="inherit" /> // Show spinner when loading
          : <Span>Submit</Span>}
                    </Button>
                    <Button type="button" variant={ButtonType.PRIMARY} onClick={handleClose} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        <Span data-sentry-element="Span" data-sentry-source-file="index.tsx">Cancel</Span>
                    </Button>
                </ButtonContainer>
                <GoldCost data-sentry-element="GoldCost" data-sentry-source-file="index.tsx">Gold Cost: 1 Gold</GoldCost>
            </ModalContainer>
        </Modal>;
};